import { createNoise2D, createNoise3D } from 'simplex-noise';
import alea from 'alea';
import { CHUNK_SIZE } from '../constants.js';
import { BlockId } from './BlockType.js';

// Nether constants
const NETHER_HEIGHT = 128;
const NETHER_CEILING = 127;
const NETHER_FLOOR = 0;
const LAVA_LEVEL = 31;
const FORTRESS_GRID = 256; // Fortress spacing

export class NetherGenerator {
  constructor(seed) {
    this.noise2D = createNoise2D(alea(seed + '-nether-2d'));
    this.noise3D = createNoise3D(alea(seed + '-nether-3d'));
    this.caveNoise = createNoise3D(alea(seed + '-nether-cave'));
    this.caveNoise2 = createNoise3D(alea(seed + '-nether-cave2'));
    this.oreNoise = createNoise3D(alea(seed + '-nether-ore'));
    this.oreNoise2 = createNoise3D(alea(seed + '-nether-ore2'));
    this.detailNoise = createNoise2D(alea(seed + '-nether-detail'));
    this.fortressNoise = createNoise2D(alea(seed + '-nether-fortress'));
    this._heightCache = new Map();
  }

  fillChunk(chunk) {
    const wx0 = chunk.cx * CHUNK_SIZE;
    const wy0 = chunk.cy * CHUNK_SIZE;
    const wz0 = chunk.cz * CHUNK_SIZE;

    for (let lz = 0; lz < CHUNK_SIZE; lz++) {
      for (let lx = 0; lx < CHUNK_SIZE; lx++) {
        const wx = wx0 + lx;
        const wz = wz0 + lz;

        for (let ly = 0; ly < CHUNK_SIZE; ly++) {
          const wy = wy0 + ly;
          const blockId = this._getBlockAt(wx, wy, wz);
          if (blockId !== BlockId.AIR) {
            chunk.setBlock(lx, ly, lz, blockId);
          }
        }
      }
    }

    // Structure pass: nether fortresses
    this._generateFortresses(chunk);
    // Glowstone clusters on ceiling
    this._generateGlowstoneClusters(chunk);
    chunk.isDirty = true;
  }

  getHeight(wx, wz) {
    // For spawning: find first open space above lava level
    return LAVA_LEVEL + 5;
  }

  // Find a safe spawn Y at given x,z
  findSpawnY(wx, wz) {
    // Scan upward from lava level to find ground
    for (let y = LAVA_LEVEL + 1; y < NETHER_HEIGHT - 10; y++) {
      const block = this._getBlockAt(wx, y, wz);
      const above1 = this._getBlockAt(wx, y + 1, wz);
      const above2 = this._getBlockAt(wx, y + 2, wz);
      if (block !== BlockId.AIR && block !== BlockId.LAVA &&
          above1 === BlockId.AIR && above2 === BlockId.AIR) {
        return y + 1;
      }
    }
    return LAVA_LEVEL + 10;
  }

  _getBlockAt(wx, wy, wz) {
    // Bedrock floor
    if (wy <= 0) return BlockId.BEDROCK;
    if (wy <= 2) {
      const v = Math.abs(this.noise3D(wx * 0.5, wy * 0.5, wz * 0.5));
      if (v < 0.5) return BlockId.BEDROCK;
    }

    // Bedrock ceiling
    if (wy >= NETHER_CEILING) return BlockId.BEDROCK;
    if (wy >= NETHER_CEILING - 2) {
      const v = Math.abs(this.noise3D(wx * 0.5 + 100, wy * 0.5, wz * 0.5 + 100));
      if (v < 0.5) return BlockId.BEDROCK;
    }

    // Get the "terrain" height profile for floor and ceiling
    const floorHeight = this._getFloorHeight(wx, wz);
    const ceilingHeight = this._getCeilingHeight(wx, wz);

    // Below floor = solid netherrack
    if (wy <= floorHeight) {
      return this._getNetherOre(wx, wy, wz);
    }

    // Above ceiling = solid netherrack
    if (wy >= ceilingHeight) {
      return this._getNetherOre(wx, wy, wz);
    }

    // Lava lakes below lava level
    if (wy <= LAVA_LEVEL) {
      return BlockId.LAVA;
    }

    // Large cave carving (3D noise to make it feel cavernous)
    const caveVal = this.caveNoise(wx * 0.035, wy * 0.06, wz * 0.035);
    const caveVal2 = this.caveNoise2(wx * 0.02, wy * 0.04, wz * 0.02);

    // Combined cave effect - creates large interconnected caverns
    if (caveVal > -0.1 && caveVal < 0.1) {
      // Thin cave tunnels
      return BlockId.AIR;
    }

    // Large open caverns
    if (caveVal2 > 0.15) {
      return BlockId.AIR;
    }

    // Smaller pockets
    if (Math.abs(caveVal) < 0.25 && caveVal2 > -0.1) {
      return BlockId.AIR;
    }

    // Soul sand patches on the floor area
    if (wy <= floorHeight + 3 && wy > floorHeight) {
      const soulVal = this.detailNoise(wx * 0.08, wz * 0.08);
      if (soulVal > 0.3) {
        return BlockId.SOUL_SAND;
      }
    }

    // Default: netherrack with possible ores
    return this._getNetherOre(wx, wy, wz);
  }

  _getFloorHeight(wx, wz) {
    const key = ((wx & 0xFFFF) << 16) | (wz & 0xFFFF);
    // Use a separate section of the cache for floor heights
    const cacheKey = key + 0x100000000;
    if (this._heightCache.has(cacheKey)) return this._heightCache.get(cacheKey);

    // Undulating floor, roughly 20-45
    let h = 0;
    h += this.noise2D(wx * 0.02, wz * 0.02) * 10;
    h += this.noise2D(wx * 0.05, wz * 0.05) * 5;
    h += this.noise2D(wx * 0.1, wz * 0.1) * 2;
    const result = Math.floor(25 + h);

    if (this._heightCache.size > 100000) this._heightCache.clear();
    this._heightCache.set(cacheKey, result);
    return result;
  }

  _getCeilingHeight(wx, wz) {
    const key = ((wx & 0xFFFF) << 16) | (wz & 0xFFFF);
    const cacheKey = key + 0x200000000;
    if (this._heightCache.has(cacheKey)) return this._heightCache.get(cacheKey);

    // Undulating ceiling, roughly 90-120
    let h = 0;
    h += this.noise2D(wx * 0.015 + 500, wz * 0.015 + 500) * 12;
    h += this.noise2D(wx * 0.04 + 500, wz * 0.04 + 500) * 6;
    const result = Math.floor(105 + h);

    if (this._heightCache.size > 100000) this._heightCache.clear();
    this._heightCache.set(cacheKey, result);
    return result;
  }

  _getNetherOre(wx, wy, wz) {
    // Nether gold ore (fairly common)
    if (wy >= 10 && wy <= 110) {
      const v = this.oreNoise(wx * 0.12, wy * 0.12, wz * 0.12);
      if (v > 0.82) return BlockId.NETHER_GOLD_ORE;
    }
    // Nether quartz ore (medium)
    if (wy >= 10 && wy <= 110) {
      const v = this.oreNoise2(wx * 0.1, wy * 0.1, wz * 0.1);
      if (v > 0.78) return BlockId.NETHER_QUARTZ_ORE;
    }
    // Gravel pockets
    if (wy >= 25 && wy <= 80) {
      const v = this.oreNoise(wx * 0.08 + 300, wy * 0.08, wz * 0.08 + 300);
      if (v > 0.88) return BlockId.GRAVEL;
    }
    return BlockId.NETHERRACK;
  }

  // --- Glowstone clusters hanging from ceiling ---
  _generateGlowstoneClusters(chunk) {
    const wx0 = chunk.cx * CHUNK_SIZE;
    const wy0 = chunk.cy * CHUNK_SIZE;
    const wz0 = chunk.cz * CHUNK_SIZE;

    // Only generate in chunks that are near the ceiling
    if (wy0 + CHUNK_SIZE < 80 || wy0 > 120) return;

    // Check a grid of potential glowstone positions in this chunk
    for (let lx = 0; lx < CHUNK_SIZE; lx += 4) {
      for (let lz = 0; lz < CHUNK_SIZE; lz += 4) {
        const wx = wx0 + lx;
        const wz = wz0 + lz;
        const chance = this.detailNoise(wx * 0.15 + 200, wz * 0.15 + 200);
        if (chance < 0.6) continue;

        const ceilingH = this._getCeilingHeight(wx, wz);

        // Place a cluster hanging down from ceiling
        const clusterSize = 2 + Math.floor(Math.abs(this.detailNoise(wx * 0.3, wz * 0.3)) * 3);
        for (let dy = 0; dy < clusterSize; dy++) {
          for (let dx = -1; dx <= 1; dx++) {
            for (let dz = -1; dz <= 1; dz++) {
              // Only some blocks in the cluster
              const r = Math.abs(this.detailNoise((wx + dx) * 0.5 + dy, (wz + dz) * 0.5 + dy));
              if (r < 0.3 && (dx !== 0 || dz !== 0)) continue;

              const bx = lx + dx;
              const by = ceilingH - dy - 1 - wy0;
              const bz = lz + dz;
              if (bx >= 0 && bx < CHUNK_SIZE && by >= 0 && by < CHUNK_SIZE && bz >= 0 && bz < CHUNK_SIZE) {
                chunk.setBlock(bx, by, bz, BlockId.GLOWSTONE);
              }
            }
          }
        }
      }
    }
  }

  // --- Nether Fortress Generation ---
  _generateFortresses(chunk) {
    const wx0 = chunk.cx * CHUNK_SIZE;
    const wz0 = chunk.cz * CHUNK_SIZE;

    // Check if any fortress center is near this chunk
    const searchDist = 40;
    const minGx = Math.floor((wx0 - searchDist) / FORTRESS_GRID);
    const maxGx = Math.floor((wx0 + CHUNK_SIZE + searchDist) / FORTRESS_GRID);
    const minGz = Math.floor((wz0 - searchDist) / FORTRESS_GRID);
    const maxGz = Math.floor((wz0 + CHUNK_SIZE + searchDist) / FORTRESS_GRID);

    for (let gx = minGx; gx <= maxGx; gx++) {
      for (let gz = minGz; gz <= maxGz; gz++) {
        // Deterministic fortress position
        const fNoise = this.fortressNoise(gx * 1.3, gz * 1.3);
        if (fNoise < 0.2) continue; // Only ~40% of grid cells have fortresses

        const fx = gx * FORTRESS_GRID + Math.floor((this.fortressNoise(gx * 0.7, gz * 0.7) + 1) * FORTRESS_GRID * 0.25);
        const fz = gz * FORTRESS_GRID + Math.floor((this.fortressNoise(gx * 0.3 + 100, gz * 0.3 + 100) + 1) * FORTRESS_GRID * 0.25);
        const fy = 50 + Math.floor(this.fortressNoise(gx * 2.1, gz * 2.1) * 10);

        this._placeFortress(chunk, fx, fy, fz);
      }
    }
  }

  _placeFortress(chunk, fcx, fcy, fcz) {
    const wx0 = chunk.cx * CHUNK_SIZE;
    const wy0 = chunk.cy * CHUNK_SIZE;
    const wz0 = chunk.cz * CHUNK_SIZE;

    // Main platform (20 x 1 x 20)
    this._placePlatform(chunk, fcx - 10, fcy, fcz - 10, 20, 20, wx0, wy0, wz0);

    // Corridor extending North
    this._placeBridge(chunk, fcx - 2, fcy, fcz - 10, 4, 30, 'z', wx0, wy0, wz0);
    // Corridor extending East
    this._placeBridge(chunk, fcx + 10, fcy, fcz - 2, 30, 4, 'x', wx0, wy0, wz0);
    // Corridor extending West
    this._placeBridge(chunk, fcx - 40, fcy, fcz - 2, 30, 4, 'x', wx0, wy0, wz0);
    // Corridor extending South
    this._placeBridge(chunk, fcx - 2, fcy, fcz + 10, 4, 30, 'z', wx0, wy0, wz0);

    // Towers at corridor ends
    this._placeTower(chunk, fcx, fcy, fcz - 38, wx0, wy0, wz0);
    this._placeTower(chunk, fcx + 38, fcy, fcz, wx0, wy0, wz0);
    this._placeTower(chunk, fcx - 38, fcy, fcz, wx0, wy0, wz0);
    this._placeTower(chunk, fcx, fcy, fcz + 38, wx0, wy0, wz0);

    // Treasure room at center platform
    this._placeTreasureRoom(chunk, fcx - 3, fcy + 1, fcz - 3, wx0, wy0, wz0);

    // Walls around main platform
    this._placeWalls(chunk, fcx - 10, fcy + 1, fcz - 10, 20, 4, 20, wx0, wy0, wz0);
  }

  _placePlatform(chunk, px, py, pz, w, d, wx0, wy0, wz0) {
    for (let dx = 0; dx < w; dx++) {
      for (let dz = 0; dz < d; dz++) {
        const wx = px + dx;
        const wz = pz + dz;
        // Floor
        this._setInChunk(chunk, wx - wx0, py - wy0, wz - wz0, BlockId.NETHER_BRICKS);
        // Underside support
        this._setInChunk(chunk, wx - wx0, py - 1 - wy0, wz - wz0, BlockId.NETHER_BRICKS);
      }
    }
  }

  _placeBridge(chunk, px, py, pz, w, d, axis, wx0, wy0, wz0) {
    const bw = axis === 'x' ? w : d;
    const bd = axis === 'x' ? d : w;

    for (let dx = 0; dx < bw; dx++) {
      for (let dz = 0; dz < bd; dz++) {
        const wx = px + dx;
        const wz = pz + dz;
        // Floor
        this._setInChunk(chunk, wx - wx0, py - wy0, wz - wz0, BlockId.NETHER_BRICKS);
        // Railings (fence posts on edges)
        if (axis === 'x' && (dz === 0 || dz === bd - 1)) {
          this._setInChunk(chunk, wx - wx0, py + 1 - wy0, wz - wz0, BlockId.FENCE);
        }
        if (axis === 'z' && (dx === 0 || dx === bw - 1)) {
          this._setInChunk(chunk, wx - wx0, py + 1 - wy0, wz - wz0, BlockId.FENCE);
        }
      }
    }
  }

  _placeTower(chunk, tx, ty, tz, wx0, wy0, wz0) {
    // 5x5 base, 10 blocks tall
    for (let dy = 0; dy <= 10; dy++) {
      for (let dx = -2; dx <= 2; dx++) {
        for (let dz = -2; dz <= 2; dz++) {
          const wx = tx + dx;
          const wy = ty + dy;
          const wz = tz + dz;

          const isWall = Math.abs(dx) === 2 || Math.abs(dz) === 2;
          const isFloor = dy === 0;
          const isTop = dy === 10;

          if (isFloor || isTop) {
            this._setForce(chunk, wx - wx0, wy - wy0, wz - wz0, BlockId.NETHER_BRICKS);
          } else if (isWall) {
            this._setForce(chunk, wx - wx0, wy - wy0, wz - wz0, BlockId.NETHER_BRICKS);
          } else {
            this._setForce(chunk, wx - wx0, wy - wy0, wz - wz0, BlockId.AIR);
          }
        }
      }
    }

    // Opening on one side
    for (let dy = 1; dy <= 3; dy++) {
      this._setForce(chunk, tx - wx0, ty + dy - wy0, tz + 2 - wz0, BlockId.AIR);
    }
  }

  _placeTreasureRoom(chunk, rx, ry, rz, wx0, wy0, wz0) {
    // 6x4x6 room
    for (let dy = 0; dy < 4; dy++) {
      for (let dx = 0; dx < 6; dx++) {
        for (let dz = 0; dz < 6; dz++) {
          const wx = rx + dx;
          const wy = ry + dy;
          const wz = rz + dz;

          const isWall = dx === 0 || dx === 5 || dz === 0 || dz === 5;
          const isFloor = dy === 0;
          const isCeiling = dy === 3;

          if (isFloor || isCeiling) {
            this._setForce(chunk, wx - wx0, wy - wy0, wz - wz0, BlockId.NETHER_BRICKS);
          } else if (isWall) {
            this._setForce(chunk, wx - wx0, wy - wy0, wz - wz0, BlockId.NETHER_BRICKS);
          } else {
            this._setForce(chunk, wx - wx0, wy - wy0, wz - wz0, BlockId.AIR);
          }
        }
      }
    }

    // Entrance (2-wide opening)
    for (let dy = 1; dy <= 2; dy++) {
      this._setForce(chunk, rx + 2 - wx0, ry + dy - wy0, rz - wz0, BlockId.AIR);
      this._setForce(chunk, rx + 3 - wx0, ry + dy - wy0, rz - wz0, BlockId.AIR);
    }

    // Chests in corners
    this._setForce(chunk, rx + 1 - wx0, ry + 1 - wy0, rz + 4 - wz0, BlockId.CHEST);
    this._setForce(chunk, rx + 4 - wx0, ry + 1 - wy0, rz + 4 - wz0, BlockId.CHEST);

    // Glowstone for lighting
    this._setForce(chunk, rx + 3 - wx0, ry + 3 - wy0, rz + 3 - wz0, BlockId.GLOWSTONE);
  }

  _placeWalls(chunk, px, py, pz, w, h, d, wx0, wy0, wz0) {
    for (let dy = 0; dy < h; dy++) {
      for (let dx = 0; dx < w; dx++) {
        for (let dz = 0; dz < d; dz++) {
          if (dx !== 0 && dx !== w - 1 && dz !== 0 && dz !== d - 1) continue;

          const wx = px + dx;
          const wy = py + dy;
          const wz = pz + dz;

          // Gaps for passage every 5 blocks
          if (dy <= 2) {
            if ((dx === Math.floor(w / 2) && (dz === 0 || dz === d - 1)) ||
                (dz === Math.floor(d / 2) && (dx === 0 || dx === w - 1))) {
              continue; // Opening
            }
          }

          this._setInChunk(chunk, wx - wx0, wy - wy0, wz - wz0, BlockId.NETHER_BRICKS);
        }
      }
    }
  }

  _setInChunk(chunk, lx, ly, lz, blockId) {
    if (lx >= 0 && lx < CHUNK_SIZE && ly >= 0 && ly < CHUNK_SIZE && lz >= 0 && lz < CHUNK_SIZE) {
      const current = chunk.getBlock(lx, ly, lz);
      if (current === BlockId.AIR || current === BlockId.LAVA) {
        chunk.setBlock(lx, ly, lz, blockId);
      }
    }
  }

  _setForce(chunk, lx, ly, lz, blockId) {
    if (lx >= 0 && lx < CHUNK_SIZE && ly >= 0 && ly < CHUNK_SIZE && lz >= 0 && lz < CHUNK_SIZE) {
      chunk.setBlock(lx, ly, lz, blockId);
    }
  }
}
